/* ------------------------------------------------------------------ */
/* Seção 3 — Assinatura e Certificado Digital                           */
/* ------------------------------------------------------------------ */
import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { StatusBadge, type Status } from './badges';

interface OpcaoCert {
  id: string;
  nome: string;
  status: Status;
  tagPrincipal?: string;
  subtitulo: string;
  descricao: string;
  usoPratico: string[];
  vantagens: string[];
  desvantagens: string[];
  quandoUsar: string;
  statusProAdv: string;
  fontes?: Array<{ label: string; url: string }>;
}

const opcoesCert: OpcaoCert[] = [
  {
    id: 'a1-server',
    nome: 'Certificado A1 — Server-side (preferido)',
    status: 'ativo',
    tagPrincipal: 'PREFERIDO PARA MVP',
    subtitulo: 'PFX/P12 armazenado no servidor, descriptografado em memória para uso.',
    descricao:
      'Certificado digital A1 do advogado (arquivo PFX/P12) armazenado de forma segura no servidor com criptografia AES-256-GCM. J04 Cert Manager já implementado — upload via Configurações, armazenamento cifrado com CERT_MASTER_KEY, uso em runtime sem exposição do arquivo. Modelo atual do ADVBOX e da maioria dos sistemas CRM jurídicos de mercado: advogado entrega o cert, sistema usa automaticamente.',
    usoPratico: [
      'Configurações → Certificado digital — tela de upload e gerenciamento do cert A1',
      'Editor de documentos (D07) — botão "Assinar com A1" em petições, contratos e declarações',
      'Peticionamento eletrônico (J05, fase futura) — envio de manifestação via J05.entregarManifestacao',
    ],
    vantagens: [
      'Automação total — sem interação do advogado por operação',
      'Já implementado no VoxisAdv (J04 Cert Manager, migration 0014)',
      'Padrão de mercado para sistemas CRM jurídicos',
      'Permite mTLS e operações server-side completas',
      'Bárbara já funciona sem cert no ADVBOX — validação que login/senha basta para consulta',
    ],
    desvantagens: [
      'Certificado A1 tem validade de 1 ano (ICP-Brasil) — precisa de renovação anual',
      'Advogado precisa ter um certificado A1 (não A3/token físico)',
      'Para peticionamento real: tribunais exigem assinatura com cert válido em runtime',
      'Responsabilidade do advogado por manter cert atualizado no sistema',
    ],
    quandoUsar: 'Para a fase de consulta processual (J05) e peticionamento futuro. É o caminho do MVP.',
    statusProAdv: 'Implementado (J04, migration 0014). CERT_MASTER_KEY aplicada em produção desde 2026-05-11.',
    fontes: [
      { label: 'ICP-Brasil — Certificados A1', url: 'https://www.iti.gov.br/icp-brasil' },
      { label: 'infosimples/infosimples Issue #615 — login + password + pkcs12 cert', url: 'https://github.com/infosimples/infosimples/issues/615' },
    ],
  },
  {
    id: 'cert-nuvem',
    nome: 'Certificado em Nuvem — BirdID / Vidaas / Soluti',
    status: 'arquivado',
    tagPrincipal: 'ARQUIVADO PARA MVP',
    subtitulo: 'Chave no HSM da AC. Exige 2FA por operação e parceria comercial cara.',
    descricao:
      'Certificados em nuvem (remoteID) onde a chave privada fica no HSM (Hardware Security Module) da Autoridade Certificadora, nunca no servidor do cliente. Para usar, o advogado precisa autenticar em cada operação (2FA via app). Isso torna automação server-side inviável para consultas em escala. Para assinatura, exige parceria comercial com o provedor (BirdID/Vidaas/Soluti). Para consulta processual: cert nem é exigido — login/senha basta.',
    usoPratico: [
      'Nenhum campo do VoxisAdv é preenchido por esta integração no momento',
      'Status arquivado — sem uso ativo no MVP',
      'A ser retomado na fase de peticionamento eletrônico, se parceria comercial for viável',
    ],
    vantagens: [
      'Maior segurança formal: chave nunca sai do HSM da AC',
      'Advogado não precisa manter arquivo PFX/P12',
      'Aceito por todos os tribunais brasileiros',
    ],
    desvantagens: [
      'Chave no HSM da AC — não dá para mTLS nem automação server-side sem bridge',
      'Para assinatura: exige parceria comercial cara com o provedor + 2FA por operação',
      'Para consulta: cert nem é exigido (login/senha basta) — 2FA continua obrigatório mesmo com cert',
      'Bárbara está SEM CERTIFICADO no ADVBOX e funciona com login/senha',
      'Custo de desenvolvimento alto sem benefício prático para o MVP',
    ],
    quandoUsar: 'Apenas quando o VoxisAdv precisar de assinatura digital real (peticionamento avançado) E quando a parceria comercial com o provedor for viável. Não é necessário para o MVP de consulta processual.',
    statusProAdv: 'Arquivado para o MVP. A ser retomado na fase de peticionamento eletrônico, se viável.',
    fontes: [
      { label: 'BirdId — Cert em nuvem', url: 'https://www.birdid.com.br/' },
      { label: 'Vidaas — Serpro', url: 'https://www.vidaas.com.br/' },
      { label: 'Soluti — Cert em nuvem', url: 'https://www.soluti.com.br/' },
    ],
  },
];

function OpcaoCard({ item }: { item: OpcaoCert }) {
  const [open, setOpen] = useState(false);

  return (
    <Card className={`overflow-hidden ${item.status === 'arquivado' ? 'opacity-75' : ''}`}>
      <button
        type="button"
        className="w-full text-left"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <CardTitle className="text-base leading-snug">{item.nome}</CardTitle>
                  {item.tagPrincipal && (
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                      item.status === 'arquivado'
                        ? 'bg-zinc-500/20 text-zinc-400'
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      {item.tagPrincipal}
                    </span>
                  )}
                </div>
                <p className="mt-0.5 text-xs text-[var(--fg-muted)]">{item.subtitulo}</p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <StatusBadge status={item.status} />
              <span className="text-[var(--fg-muted)] text-sm select-none">
                {open ? '▲' : '▼'}
              </span>
            </div>
          </div>
        </CardHeader>
      </button>

      {open && (
        <CardContent className="border-t border-[var(--card-border)] pt-4 space-y-5">
          <p className="text-sm text-[var(--fg-muted)] leading-relaxed">{item.descricao}</p>

          <div className="rounded-lg bg-[var(--surface-muted)]/60 p-3 text-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--fg-muted)] mb-1">Status no VoxisAdv</p>
            <p className="text-[var(--fg)]">{item.statusProAdv}</p>
          </div>

          {item.usoPratico.length > 0 && (
            <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-blue-400">
                <span>◈</span> Uso prático no app
              </p>
              <ul className="space-y-1.5">
                {item.usoPratico.map((txt, i) => (
                  <li key={i} className="flex gap-2 text-sm text-[var(--fg)]">
                    <span className="mt-0.5 shrink-0 text-blue-400">•</span>
                    {txt}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-green-400">
                <span>✓</span> Vantagens
              </p>
              <ul className="space-y-1.5">
                {item.vantagens.map((txt, i) => (
                  <li key={i} className="flex gap-2 text-sm text-[var(--fg)]">
                    <span className="mt-0.5 shrink-0 text-green-400">•</span>
                    {txt}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-red-400">
                <span>✕</span> Desvantagens
              </p>
              <ul className="space-y-1.5">
                {item.desvantagens.map((txt, i) => (
                  <li key={i} className="flex gap-2 text-sm text-[var(--fg)]">
                    <span className="mt-0.5 shrink-0 text-red-400">•</span>
                    {txt}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--accent)]">
              Quando usar
            </p>
            <p className="text-sm text-[var(--fg-muted)] leading-relaxed">{item.quandoUsar}</p>
          </div>

          {item.fontes && item.fontes.length > 0 && (
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--accent)]">
                <span>↗</span> Fontes
              </p>
              <ul className="space-y-1.5">
                {item.fontes.map((fonte) => (
                  <li key={fonte.url} className="flex gap-2 text-sm">
                    <span className="mt-0.5 shrink-0 text-[var(--accent)]">•</span>
                    <a
                      href={fonte.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--accent)] hover:underline break-all"
                    >
                      {fonte.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export function SecaoAssinatura() {
  return (
    <section aria-label="Assinatura e certificado digital">
      <div className="mb-6 space-y-2">
        <div className="flex items-center gap-2">
          <span className="rounded bg-[var(--surface-muted)] px-2 py-0.5 font-mono text-xs font-bold text-[var(--fg-muted)]">
            SEÇÃO 3
          </span>
          <h2 className="text-xl font-bold tracking-tight">Assinatura e Certificado Digital</h2>
        </div>
        <p className="max-w-2xl text-sm text-[var(--fg-muted)] leading-relaxed">
          Para consulta processual no MVP, certificado não é obrigatório — login/senha basta (validado com Bárbara no ADVBOX). Para peticionamento futuro, A1 server-side é o caminho preferido. Cert em nuvem foi arquivado para o MVP por exigir 2FA por operação e parceria comercial cara.
        </p>
      </div>

      <div className="space-y-3">
        {opcoesCert.map((item) => (
          <OpcaoCard key={item.id} item={item} />
        ))}
      </div>
    </section>
  );
}
